/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { createSlug, LISTING_PAGE_PARAM_TYPE_EDIT } from '../../../../util/urlHelpers';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  LinkedLogo,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const AboutUsLink = () => {
  return (
    <NamedLink name="CMSPage" params={{ pageId: 'about' }} className={css.navigationLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.about" />
      </span>
    </NamedLink>
  );
};

const TutorLink = () => {
  return (
    <NamedLink name="tutorPage" params={{ pageId: 'tutor' }} className={css.navigationLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.tutor" />
      </span>
    </NamedLink>
  );
};

const ContactUsLink = () => {
  return (
    <NamedLink
      name="CMSPage"
      params={{ pageId: 'contact-us' }}
      className={classNames(css.navigationLink, css.contactUsLink)}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.contact" />
      </span>
    </NamedLink>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.notificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    notificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    intl,
    config,
    currentUserHasListings,
    currentUserListing,
    location,
  } = props;

  const marketplaceName = config.marketplaceName;
  const user = ensureCurrentUser(currentUser);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const to = !!location
      ? { state: { from: `${location.pathname}${location.search}${location.hash}` } }
      : {};
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink} to={to}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink} to={to}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <LinkedLogo
              className={css.logoLink}
              layout="mobile"
              alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
          </div>

          <div className={css.customLinksWrapper}>
            {extraLinks}
            <TutorLink />
            <AboutUsLink />
            <ContactUsLink />
          </div>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>{login}</div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={notificationCount} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  const currentUserListingTitle = currentUserListing?.attributes?.title;
  const currentUserListingId = currentUserListing?.id?.uuid;
  const slug = currentUserListingTitle && createSlug(currentUserListingTitle);

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    currentUserHasListings && !!currentUserListingId && !!currentUserListingTitle ? (
      <NamedLink
        className={classNames(css.navigationLink, currentPageClass('EditListingPage'))}
        name="EditListingPage"
        params={{
          id: currentUserListingId,
          slug,
          type: LISTING_PAGE_PARAM_TYPE_EDIT,
          tab: 'details',
        }}
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.yourListingLink" />
      </NamedLink>
    ) : (
      <NamedLink
        className={classNames(css.navigationLink, currentPageClass('EditListingPage'))}
        name="EditListingPage"
        params={{
          slug: 'draft',
          id: '00000000-0000-0000-0000-000000000000',
          type: 'new',
          tab: 'details',
        }}
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.createListingLink" />
      </NamedLink>
    )
  ) : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(
              css.navigationLink,
              css.inbox,
              currentPageClass(`InboxPage:${inboxTab}`)
            )}
            name="InboxPage"
            params={{ tab: inboxTab }}
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {manageListingsLinkMaybe}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        {/* <div className={css.spacer} /> */}
      </div>
      {/* <div className={css.footer}>{createListingsLinkMaybe}</div> */}
    </div>
  );
};

export default TopbarMobileMenu;
